import { useCallback } from "react";

import { useSelector } from "react-redux";

import { useObjectMemo } from "../../../hooks";
import { useAppDispatch } from "../utils/useAppDispatch.hook";
import { actions } from "../slices/user.slice";
import { useRequestWithFeedback } from "../../../composites/request/request.hooks";

import {
  achDualApprovalRequired,
  dualApprovalRequired,
  isBusinessUser,
  selectUser,
} from "./selectors";

import type { BootstrapFetchRequest } from "../../bootstrap/bootstrap.types";

export const useUser = () => {
  return useSelector(selectUser);
};

export const useIsBusinessUser = () => {
  return useSelector(isBusinessUser);
};

export const useFetchUser = (): BootstrapFetchRequest => {
  const { send, loading, status } =
    useRequestWithFeedback<API.GetUserResponse>();

  const dispatch = useAppDispatch();

  const fetch = useCallback(() => {
    send({
      action: {
        url: "me/",
        options: {
          method: "GET",
        },
      },
      onData: ({ users: [user] }) => {
        dispatch(actions.upsert(user));
      },
    });
  }, [dispatch, send]);

  const user = useUser();

  return useObjectMemo({
    send: fetch,
    loading,
    error: status === "error",
    hasData: user !== null,
  });
};

/**
 * Safely gets the user's first and last name, or returns an
 * empty string if either are missing.
 */
export const useCurrentUserFullName = (): string => {
  const user = useUser();
  const { first_name: firstName, last_name: lastName } = user || {};
  return firstName && lastName ? `${firstName} ${lastName}` : "";
};

export const useIsDualApprovalRequired = () => {
  return useSelector(dualApprovalRequired);
};

export const useIsACHDualApprovalRequired = () => {
  return useSelector(achDualApprovalRequired);
};
